/**
 * Click-to-magnify product image zoom for Shopify Horizon.
 *
 * Click a product image once to activate the magnified, cursor-tracking
 * crop. Click again, click anywhere outside the image, or move the cursor
 * off the image to turn it off.
 *
 * Desktop only (mouse/trackpad users). On touch devices this script does
 * nothing, so Horizon's native tap-to-fullscreen / pinch-zoom (if you have
 * the "Image zoom" block setting turned on) keeps working untouched.
 *
 * Note: this intercepts the click before it reaches Horizon's own
 * click-to-fullscreen "Image zoom" dialog, so the two won't both fire on
 * the same click.
 */
(function () {
  // How much to magnify. 2 = 200%, 3 = 300%, etc.
  const ZOOM_LEVEL = 2;

  // Only activate for devices with a precise pointer AND real hover support
  // (i.e. desktop mice/trackpads — not touchscreens).
  const DESKTOP_QUERY = '(hover: hover) and (pointer: fine)';

  function isDesktop() {
    return window.matchMedia(DESKTOP_QUERY).matches;
  }

  // True only on product pages (URLs like /products/your-tee, including
  // language/market prefixes such as /en-ca/products/your-tee).
  function isProductPage() {
    return window.location.pathname.includes('/products/');
  }

  // Tracks every image that currently has zoom turned on.
  const activeHosts = new Set();

  /**
   * Wires up one product image for click-to-zoom.
   * @param {HTMLElement} mediaEl - the `.product-media` wrapper div
   * @param {HTMLImageElement} img - the product image inside it
   */
  function initZoom(mediaEl, img) {
    if (mediaEl.dataset.hoverZoomReady) return;
    mediaEl.dataset.hoverZoomReady = 'true';
    mediaEl.classList.add('hover-zoom-host');

    const pane = document.createElement('div');
    pane.className = 'hover-zoom-pane';
    mediaEl.appendChild(pane);

    let rafId = null;

    function highResSrc() {
      // Horizon already renders a large (3840px) version of every product
      // image for its own zoom dialog — reuse that instead of fetching a
      // separate asset.
      return img.getAttribute('data_max_resolution') || img.currentSrc || img.src;
    }

    function updatePane(clientX, clientY) {
      const rect = mediaEl.getBoundingClientRect();
      const x = ((clientX - rect.left) / rect.width) * 100;
      const y = ((clientY - rect.top) / rect.height) * 100;
      pane.style.backgroundPosition = `${x}% ${y}%`;
    }

    function handleMove(event) {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        updatePane(event.clientX, event.clientY);
      });
    }

    function activate(clientX, clientY) {
      pane.style.backgroundImage = `url("${highResSrc()}")`;
      pane.style.backgroundSize = `${ZOOM_LEVEL * 100}%`;
      mediaEl.classList.add('is-zooming');
      updatePane(clientX, clientY);
      mediaEl.addEventListener('pointermove', handleMove);
      activeHosts.add(mediaEl);
    }

    function deactivate() {
      mediaEl.classList.remove('is-zooming');
      mediaEl.removeEventListener('pointermove', handleMove);
      activeHosts.delete(mediaEl);
    }

    mediaEl.deactivateZoom = deactivate;

    mediaEl.addEventListener('click', (event) => {
      // Stop this click from also opening Horizon's built-in
      // click-to-fullscreen zoom dialog on the same image.
      event.stopPropagation();

      if (activeHosts.has(mediaEl)) {
        deactivate();
      } else {
        activate(event.clientX, event.clientY);
      }
    });

    // Reset back to the normal image as soon as the cursor leaves it.
    mediaEl.addEventListener('pointerleave', deactivate);
  }

  // Clicking anywhere outside an active zoomed image closes it.
  document.addEventListener('click', (event) => {
    activeHosts.forEach((mediaEl) => {
      if (!mediaEl.contains(event.target)) {
        mediaEl.deactivateZoom();
      }
    });
  });

  /**
   * Finds product images inside `root` and wires them up.
   * @param {ParentNode} root
   */
  function scan(root) {
    if (!isDesktop()) return;
    if (!isProductPage()) return;
    if (typeof root.querySelectorAll !== 'function') return;

    // Only the main product page gallery — not collection cards, featured
    // product sections, quick-add popups, etc.
    root.querySelectorAll('.product-information__media .product-media-container--image .product-media__image').forEach((img) => {
      const mediaEl = img.closest('.product-media');
      if (mediaEl) initZoom(mediaEl, img);
    });
  }

  document.addEventListener('DOMContentLoaded', () => scan(document));

  // Horizon swaps in new media elements when a shopper changes variants
  // (e.g. picks a different color). Watch for that and wire up new images.
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) scan(/** @type {Element} */ (node));
      });
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();
